"use client";

import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Loader2,
  Globe,
  Lock,
  X,
  Tag,
  Info,
  Award,
  Hash,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MentionInput } from "@/components/MentionInput";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/auth-context";
import { useToast } from "@/hooks/use-toast";
import { resolveFieldSlug } from "@/lib/forum-fields";
import { CertificationAutocomplete } from "@/components/certification-autocomplete";
import { ForumPhotoPicker } from "@/components/forum-photos";

interface ForumCategory {
  id: string;
  slug: string;
  name: string;
  topicCount: number;
}

interface CreateTopicDialogProps {
  categoryId?: string;
  categoryName?: string;
  defaultCertification?: string;
  trigger?: React.ReactNode;
  onTopicCreated?: (topic: any) => void;
}

export function CreateTopicDialog({
  categoryId,
  categoryName,
  defaultCertification,
  trigger,
  onTopicCreated,
}: CreateTopicDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<"PUBLIC" | "CL_COMMUNITY">("PUBLIC");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // Category combobox state
  const [categoryInput, setCategoryInput] = useState(categoryName || "");
  // selectedCategory is the field id sent to the API — never a free-text cert name
  const [selectedCategory, setSelectedCategory] = useState(categoryId || "");
  const [selectedCertification, setSelectedCertification] = useState(defaultCertification || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [forumCategories, setForumCategories] = useState<ForumCategory[]>([]);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load forum categories + certification list when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    setDataLoading(true);
    Promise.all([
      fetch("/api/forum/categories").then((r) => (r.ok ? r.json() : null)),
      fetch("/api/certifications/list").then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([catData, certData]) => {
        if (catData?.categories?.length) setForumCategories(catData.categories);
        if (Array.isArray(certData?.certifications)) setCertifications(certData.certifications);
      })
      .catch(() => {})
      .finally(() => setDataLoading(false));
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        if (categoryInput.trim() && !selectedCategory) {
          commitTypedValue(categoryInput.trim());
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [categoryInput, selectedCategory]);

  // Filtered suggestions
  const q = categoryInput.toLowerCase().trim();
  const matchedCategories = forumCategories.filter((c) =>
    q === "" || c.name.toLowerCase().includes(q)
  );
  const matchedCerts = certifications.filter((c) =>
    q !== "" && c.toLowerCase().includes(q) &&
    !forumCategories.some((fc) => fc.name.toLowerCase() === c.toLowerCase())
  );
  const exactMatch =
    forumCategories.some((c) => c.name.toLowerCase() === q) ||
    certifications.some((c) => c.toLowerCase() === q);

  const fieldIdForSlug = (slug: string) =>
    forumCategories.find((c) => c.slug === slug)?.id ||
    forumCategories.find((c) => c.slug === "general")?.id ||
    "";

  const commitTypedValue = (value: string) => {
    const matchField = forumCategories.find(
      (c) => c.name.toLowerCase() === value.toLowerCase() || c.slug === value.toLowerCase(),
    );
    if (matchField) {
      setSelectedCategory(matchField.id);
      setCategoryInput(matchField.name);
      return;
    }
    const matchCert = certifications.find((c) => c.toLowerCase() === value.toLowerCase());
    const label = matchCert || value;
    setSelectedCertification(label);
    setSelectedCategory(fieldIdForSlug(resolveFieldSlug(label)));
    setCategoryInput(label);
  };

  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryInput(e.target.value);
    setSelectedCategory(""); // reset until user picks or blurs
    setShowDropdown(true);
  };

  const handleSelectForumCategory = (cat: ForumCategory) => {
    setCategoryInput(cat.name);
    setSelectedCategory(cat.id);
    setShowDropdown(false);
  };

  const handleSelectCert = (cert: string) => {
    setCategoryInput(cert);
    setSelectedCertification(cert);
    setSelectedCategory(fieldIdForSlug(resolveFieldSlug(cert)));
    setShowDropdown(false);
  };

  const handleCategoryBlur = () => {
    setTimeout(() => {
      if (!selectedCategory && categoryInput.trim()) {
        commitTypedValue(categoryInput.trim());
      }
    }, 150);
  };

  const clearCategory = () => {
    setCategoryInput("");
    setSelectedCategory("");
    setShowDropdown(true);
    inputRef.current?.focus();
  };

  const resetForm = (): void => {
    setTitle("");
    setContent("");
    setPhotos([]);
    setCategoryInput(categoryName || "");
    setSelectedCategory(categoryId || "");
    setSelectedCertification(defaultCertification || "");
    setVisibility("PUBLIC");
    setTags([]);
    setTagInput("");
    setError("");
    setShowDropdown(false);
  };

  const handleClose = (): void => {
    resetForm();
    setIsOpen(false);
  };

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 5) {
      setTags([...tags, trimmedTag]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  const effectiveCategory =
    selectedCategory ||
    categoryId ||
    fieldIdForSlug(resolveFieldSlug(categoryInput.trim() || "general"));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) { setError("Please enter a topic title"); return; }
    if (!content.trim()) { setError("Please enter topic content"); return; }
    if (!effectiveCategory) { setError("Please pick a field"); return; }
    if (!selectedCertification.trim()) { setError("Please pick an exam / certification"); return; }

    setIsLoading(true);
    try {
      const response = await fetch("/api/forums/topics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          categoryId: effectiveCategory,
          certification: selectedCertification.trim(),
          visibility,
          imageUrls: photos,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to create topic");
      }

      const data = await response.json();
      const newTopic = data.topic ?? data;
      toast({
        title: "Topic created!",
        description: `"${title.trim()}" has been posted successfully.`,
      });
      onTopicCreated?.(newTopic);
      handleClose();
    } catch (err: any) {
      toast({
        title: "Could not create topic",
        description: "We couldn't create that topic. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-primary-600 hover:bg-primary-700">
            <Plus className="h-4 w-4 mr-2" />
            Create New Topic
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Topic</DialogTitle>
          <DialogDescription>
            Start a new discussion in the CertLynx community
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Category combobox */}
          <div className="space-y-2">
            <Label htmlFor="category-input">Category</Label>
            <div className="relative">
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="category-input"
                  ref={inputRef}
                  autoComplete="off"
                  placeholder={
                    dataLoading
                      ? "Loading categories…"
                      : "Search a field or certification…"
                  }
                  value={categoryInput}
                  onChange={handleCategoryInputChange}
                  onFocus={() => !categoryId && setShowDropdown(true)}
                  onBlur={handleCategoryBlur}
                  onKeyDown={(e) => e.key === "Escape" && setShowDropdown(false)}
                  disabled={!!categoryId || isLoading}
                  className="pl-10 pr-10"
                />
                {categoryInput && !categoryId && (
                  <button
                    type="button"
                    onClick={clearCategory}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {showDropdown && !categoryId && (
                <div
                  ref={dropdownRef}
                  className="absolute z-[200] w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-72 overflow-y-auto"
                >
                  {/* Existing forum categories */}
                  {matchedCategories.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                        Fields
                      </div>
                      {matchedCategories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); handleSelectForumCategory(cat); }}
                          className="w-full px-4 py-2 text-left hover:bg-muted text-sm flex items-center justify-between gap-2 border-b border-border last:border-0"
                        >
                          <span className="flex items-center gap-2">
                            <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            {cat.name}
                          </span>
                          {cat.topicCount > 0 && (
                            <span className="text-xs text-muted-foreground shrink-0">
                              {cat.topicCount} topic{cat.topicCount !== 1 ? "s" : ""}
                            </span>
                          )}
                        </button>
                      ))}
                    </>
                  )}

                  {/* Certification suggestions (only when typing) */}
                  {matchedCerts.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                        Certifications
                      </div>
                      {matchedCerts.slice(0, 30).map((cert) => (
                        <button
                          key={cert}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); handleSelectCert(cert); }}
                          className="w-full px-4 py-2 text-left hover:bg-muted text-sm flex items-center gap-2 border-b border-border last:border-0"
                        >
                          <Award className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          {cert}
                        </button>
                      ))}
                    </>
                  )}

                  {q !== "" && !exactMatch && matchedCategories.length === 0 && matchedCerts.length === 0 && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        commitTypedValue(categoryInput.trim());
                        setShowDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-muted text-sm flex items-center gap-2 border-t border-border"
                    >
                      <Award className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      Post “{categoryInput.trim()}” in the matching field
                    </button>
                  )}

                  {matchedCategories.length === 0 && matchedCerts.length === 0 && q === "" && (
                    <p className="px-4 py-3 text-sm text-muted-foreground text-center">
                      Start typing to search fields or certifications
                    </p>
                  )}
                </div>
              )}
            </div>
            {categoryId ? (
              <p className="text-sm text-muted-foreground">Posting in: {categoryName}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Pick a field. The exam tag below is required and does not create a new forum.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Exam / certification</Label>
            <CertificationAutocomplete
              value={selectedCertification}
              onChange={(value) => {
                setSelectedCertification(value);
                if (value && !selectedCategory && !categoryId) {
                  setSelectedCategory(fieldIdForSlug(resolveFieldSlug(value)));
                  setCategoryInput(value);
                }
              }}
              placeholder="Required — e.g. NCLEX-RN, CPA, PMP"
            />
          </div>

          {/* Topic Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Topic Title</Label>
            <Input
              id="title"
              placeholder="Enter a descriptive title for your topic"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading}
              maxLength={100}
            />
            <div className="text-xs text-muted-foreground text-right">
              {title.length}/100 characters
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label>Topic Visibility</Label>
              <div className="group relative">
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-white border border-border text-foreground text-sm rounded-md shadow-lg w-64 opacity-0 group-hover:opacity-100 transition-opacity z-[300] pointer-events-none">
                  Choose whether your topic should be visible to everyone or
                  only to CertLynx community members.
                </div>
              </div>
            </div>
            <RadioGroup
              value={visibility}
              onValueChange={(value) => setVisibility(value as "PUBLIC" | "CL_COMMUNITY")}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="PUBLIC" id="public" className="mt-1" />
                <div className="flex-1 space-y-1">
                  <Label htmlFor="public" className="flex items-center gap-2 cursor-pointer">
                    <Globe className="h-4 w-4 text-green-600" />
                    <span className="font-medium">Public</span>
                    <Badge variant="secondary" className="ml-2">Recommended</Badge>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Visible to everyone, including visitors who aren&apos;t logged in. Great for sharing knowledge publicly.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                <RadioGroupItem value="CL_COMMUNITY" id="private" className="mt-1" />
                <div className="flex-1 space-y-1">
                  <Label htmlFor="private" className="flex items-center gap-2 cursor-pointer">
                    <Lock className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">CertLynx Community Only</span>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Only visible to logged-in CertLynx members. Use this for discussions that require community context.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Topic Content */}
          <div className="space-y-2">
            <Label htmlFor="content">Topic Content</Label>
            <MentionInput
              id="content"
              multiline
              rows={8}
              menuPlacement="above"
              value={content}
              onChange={setContent}
              placeholder="Share your thoughts, ask questions, or start a discussion. Type @ to mention a member."
              disabled={isLoading}
              className="professional-input rounded-md resize-none min-h-[80px] border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <ForumPhotoPicker urls={photos} onChange={setPhotos} disabled={isLoading} />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">Tags (Optional)</Label>
            <div className="space-y-2">
              <Input
                id="tags"
                placeholder="Add tags to help others find your topic (press Enter or comma to add)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagKeyPress}
                onBlur={() => addTag(tagInput)}
                disabled={isLoading || tags.length >= 5}
                maxLength={20}
              />
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Add up to 5 tags to help categorize your topic ({tags.length}/5)
              </p>
            </div>
          </div>

          {/* Guidelines Notice */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Please remember:</strong> Be respectful, search existing topics before posting, use clear titles, and follow our community guidelines. Topics that violate guidelines may be moderated or removed.
            </AlertDescription>
          </Alert>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !title.trim() || !content.trim() || !effectiveCategory}
              className="flex-1 bg-primary-600 hover:bg-primary-700"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Topic
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
